import { ISSUER_CONDITION_LABELS, RECEPTOR_CONDITION_LABELS, type DeliveryNoteDTO } from '@bifeai/shared';
import { formatDate, formatMoney } from './format';

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const qty = (unitType: string, q: number) =>
  unitType === 'WEIGHT' ? `${(q / 1000).toLocaleString('es-AR', { maximumFractionDigits: 3 })} kg` : `${q} u.`;
const iva = (c: string | null) => (c ? (({ ...RECEPTOR_CONDITION_LABELS, ...ISSUER_CONDITION_LABELS } as Record<string, string>)[c] ?? c) : '');
const cuit = (c: string | null) => (c && c.length === 11 ? `${c.slice(0, 2)}-${c.slice(2, 10)}-${c.slice(10)}` : (c ?? ''));

/** Un comprobante de entrega (documento interno, no válido como factura). Solo datos del local y del cliente. */
function page(n: DeliveryNoteDTO, prices: boolean): string {
  const rows = n.items
    .map(
      (i) =>
        `<tr><td>${esc(i.productName)}</td><td class="r">${qty(i.unitType, i.qty)}</td>${
          prices
            ? `<td class="r">${formatMoney(i.unitPriceCents)}${i.unitType === 'WEIGHT' ? '/kg' : ''}</td><td class="r">${formatMoney(i.lineTotalCents)}</td>`
            : ''
        }</tr>`,
    )
    .join('');
  return `<section class="note">
  <div class="top">
    <div>
      <div class="issuer">${esc(n.issuer.name)}</div>
      ${n.issuer.cuit ? `<div>CUIT ${cuit(n.issuer.cuit)}${n.issuer.ivaCondition ? ` · ${esc(iva(n.issuer.ivaCondition))}` : ''}</div>` : ''}
      ${n.issuer.address ? `<div>${esc(n.issuer.address)}</div>` : ''}
      ${n.issuer.phone ? `<div>Tel. ${esc(n.issuer.phone)}</div>` : ''}
    </div>
    <div class="x"><b>X</b><small>Documento no válido como factura</small></div>
    <div class="meta">
      <div class="title">Comprobante de entrega</div>
      <div>N° ${String(n.number).padStart(8, '0')}</div>
      <div>Fecha ${formatDate(n.date)}</div>
    </div>
  </div>
  <div class="cust">
    <div><b>Cliente:</b> ${esc(n.customer.name)}${n.customer.tradeName && n.customer.tradeName !== n.customer.name ? ` (${esc(n.customer.tradeName)})` : ''}</div>
    <div>${n.customer.cuit ? `CUIT ${cuit(n.customer.cuit)} · ` : ''}${esc(iva(n.customer.ivaCondition))}</div>
    ${n.customer.address ? `<div><b>Entrega en:</b> ${esc(n.customer.address)}</div>` : ''}
  </div>
  <table><thead><tr><th>Producto</th><th class="r">Cantidad</th>${prices ? '<th class="r">Precio</th><th class="r">Importe</th>' : ''}</tr></thead><tbody>${rows}</tbody>
  ${prices ? `<tfoot><tr><td colspan="3" class="r"><b>Total</b></td><td class="r"><b>${formatMoney(n.totalCents)}</b></td></tr></tfoot>` : ''}</table>
  ${n.notes ? `<div class="notes">${esc(n.notes)}</div>` : ''}
  <div class="sign"><div>Recibí conforme · Firma</div><div>Aclaración</div><div>DNI</div></div>
</section>`;
}

/** Abre la impresión de uno o varios comprobantes de entrega (uno por hoja). */
export function printDeliveryNotes(notes: DeliveryNoteDTO[], prices: boolean): void {
  const w = window.open('', '_blank', 'width=820,height=980');
  if (!w) return;
  w.document.write(`<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Comprobante de entrega</title>
<style>
@page{size:A4;margin:14mm}body{font:12.5px system-ui,sans-serif;color:#111;margin:0}
.note{page-break-after:always}.note:last-child{page-break-after:auto}
.top{display:grid;grid-template-columns:1fr auto 1fr;gap:12px;align-items:start;border:1px solid #333;padding:10px}
.issuer{font-size:16px;font-weight:700}.meta{text-align:right}.title{font-size:15px;font-weight:700}
.x{border:1px solid #333;width:70px;text-align:center;padding:4px}.x b{display:block;font-size:30px;line-height:1}.x small{font-size:8.5px}
.cust{border:1px solid #333;border-top:0;padding:8px 10px;display:grid;gap:2px}
table{width:100%;border-collapse:collapse;margin-top:10px}th,td{border:1px solid #999;padding:6px;text-align:left}th{background:#f1f1f1}.r{text-align:right}
.notes{margin-top:8px;font-style:italic}
.sign{display:grid;grid-template-columns:2fr 1.5fr 1fr;gap:18px;margin-top:46px}.sign div{border-top:1px solid #333;padding-top:4px;font-size:11px}
</style></head><body>${notes.map((n) => page(n, prices)).join('')}<script>window.onload=()=>window.print()</script></body></html>`);
  w.document.close();
}
